import { useFetch } from "@/hooks/useFetch";
import { useCallback } from "react";
import { APIResponse } from '@/interfaces/common.interface';
import { API_BASE_URL } from '@/constants/app.constant';
import useAuthService from "./auth.service";
import { GroupInfo } from "@/interfaces/user.interface";
import { getUserGroups } from "./ms-graph-api.service";
import { msalApp } from "@/constants/auth.constant";

/**
 * useTokenUsageApi hook provides token usage and credit management API functions.
 * Handles token initialization and credit retrieval for user groups.
 * 
 * @hook
 * @returns {Object} Token usage API methods
 * @returns {Function} returns.initializeTokenData - Initialize token data for user groups
 * @returns {Function} returns.getTokenCredits - Get token credits for user groups
 * 
 * @example
 * ```tsx
 * const { initializeTokenData, getTokenCredits } = useTokenUsageApi();
 * 
 * await initializeTokenData();
 * const credits = await getTokenCredits();
 * ```
 */
export const useTokenUsageApi = () => {

  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const TOKEN_USAGE_API_BASE_URL = API_BASE_URL + "/commons/token";

  /**
   * Initializes token data for user groups from Microsoft Graph.
   * 
   * @returns {Promise<APIResponse<any>>} Initialization response
   * @throws {Error} If token is invalid
   */
  const initializeTokenData = useCallback(
    async () => {
      const token = await getUserAccessToken();
      const groups = await getUserGroups(msalApp);
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.post<APIResponse<any>>(
        `${TOKEN_USAGE_API_BASE_URL}/initialize`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: {
            'groups': groups.map((grp:any)=>grp.id)
          }
        }
      );
    },
    [fetchService]
  );


  /**
   * Gets token credits/usage information for user groups.
   * 
   * @returns {Promise<APIResponse<GroupInfo[]>>} Array of group info with token usage
   * @throws {Error} If token is invalid
   */
  const getTokenCredits = useCallback(
    async () => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.get<APIResponse<GroupInfo[]>>(
        `${TOKEN_USAGE_API_BASE_URL}/credits`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );
    },
    [fetchService]
  );

  return { initializeTokenData, getTokenCredits };
};
